import { memo } from "react";
import { useStore } from "../../store/globalState";
import { useTermsDraft } from "../../store/useTermsDraftStore";
import { useNavigate } from "react-router-dom";
import { Trans, useTranslation } from "react-i18next";
import { BodyCopy } from "../../components/BodyCopy";
import {
  AuraLayout,
  Button,
  GradientText,
  Layout,
  PageTitle,
} from "lola-framework-ui-test";
import { FlowHeroMark } from "../../components/FlowHeroMark";
import { LogoPlaceholder } from "../../components/LogoPlaceholder";
import KapitalRLogo from "../../assets/KapitalLogoR.svg";
import { M2M_ROUTES } from "../../constants";
import { ScrollFadeIndicator } from "../../components/ScrollFadeIndicator";

type LandingFlavor = "a" | "b";

interface LandingPageProps {
  flavor?: LandingFlavor;
}

/**
 * M2M landing with E-Sign + Terms checkboxes (gallery-safe).
 *
 * Both flavors share the agreements layout and the `flow-hero` mark slot, so
 * the disc center and the gap to the title match the other widgets. Flavor A
 * carries the partner logo (placeholder in the gallery) and a PageTitle;
 * flavor B carries the Kapital R brand mark and the “start transferring” copy.
 * Continue goes to Transfer A. Legal links use this flow's Legal screen.
 */
export const LandingPage = memo(({ flavor = "a" }: LandingPageProps) => {
  const { t } = useTranslation("m2m");
  const { theme } = useStore();
  const colorConfig = theme.whiteView;
  const { backgroundBtn, textColorBtn, title, bodyCopy, backgroundIcon } =
    colorConfig;
  const navigate = useNavigate();

  const {
    isESignChecked,
    isTermsChecked,
    setESignChecked: setIsESignChecked,
    setTermsChecked: setIsTermsChecked,
    reset: resetTermsDraft,
  } = useTermsDraft("m2m-landing");

  const isFlavorB = flavor === "b";

  const content = isFlavorB ? (
    <section className="flow-hero">
      {/* Brand mark, so it keeps the glass disc instead of the elevated one. */}
      <div className="flow-hero-mark">
        <img
          src={KapitalRLogo}
          alt="app logo"
          className="landing-logo gradient-card glass"
        />
      </div>
      <section className="landing-container">
        <GradientText
          as="h1"
          textColor={title}
          className="highlight text-centered"
        >
          <Trans i18nKey="landing.startTransferring" ns="m2m" />
        </GradientText>
        <BodyCopy textColor={bodyCopy}>{t("landing.followSteps2")}</BodyCopy>
      </section>
    </section>
  ) : (
    <section className="flow-hero">
      <FlowHeroMark backgroundIcon={backgroundIcon}>
        <LogoPlaceholder
          className="welcome-logo-placeholder"
          color={bodyCopy}
        />
      </FlowHeroMark>
      <section className="landing-container">
        <PageTitle
          highlight={
            <>
              {t("landing.greeting")} <br /> {t("landing.toTransfer")}
            </>
          }
          highlightColor={title}
          secudnary={
            <>
              {t("landing.moneyFrom")} <br />
              {t("landing.usAccount")}
            </>
          }
          secudnaryColor={title}
        />
        <BodyCopy textColor={bodyCopy}>
          {t("landing.followSteps")}
          <br />
          {t("landing.madeSimple")}
        </BodyCopy>
      </section>
    </section>
  );

  const contentClassName =
    "landing-section landing-agreements-section flow-hero-section";
  const layoutClassName = `white-view-background landing-agreements-layout flow-landing-page flavor-${flavor}`;

  return (
    <AuraLayout colorConfig={colorConfig} className={layoutClassName}>
      <Layout.Content className={contentClassName}>
        <ScrollFadeIndicator color={colorConfig.bodyCopy} />
        {content}
      </Layout.Content>
      <Layout.Footer>
        <section className="footer-section">
          <section className="landing-policies">
            <span
              className={`policy-checkbox${
                isESignChecked ? "" : " policy-checkbox--required"
              }`}
            >
              <input
                id={`m2m-e-sign-${flavor}`}
                type="checkbox"
                checked={isESignChecked}
                onChange={(e) => setIsESignChecked(e.target.checked)}
              />
            </span>
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label htmlFor={`m2m-e-sign-${flavor}`}>
                {t("landing.footer.esign-content")}
              </label>
              <GradientText
                as="label"
                className="link"
                textColor={title}
                onClick={() => navigate(`${M2M_ROUTES.LEGAL}?doc=esign`)}
              >
                {t("landing.footer.esign-link")}
              </GradientText>
            </BodyCopy>
          </section>
          <section className="landing-policies">
            <span
              className={`policy-checkbox${
                isTermsChecked ? "" : " policy-checkbox--required"
              }`}
            >
              <input
                id={`m2m-terms-${flavor}`}
                type="checkbox"
                checked={isTermsChecked}
                onChange={(e) => setIsTermsChecked(e.target.checked)}
              />
            </span>
            <BodyCopy className="footer-text terms-text footer-link muted-foreground">
              <label htmlFor={`m2m-terms-${flavor}`}>
                {t("landing.footer.terms-content")}
              </label>
              <GradientText
                as="label"
                className="link"
                textColor={title}
                onClick={() =>
                  navigate(`${M2M_ROUTES.LEGAL}?exclude=esign`)
                }
              >
                {t("landing.footer.terms-link")}
              </GradientText>
              <label>{t("landing.footer.terms-content-2")}</label>
            </BodyCopy>
          </section>
          <Button
            size="large"
            disabled={!isTermsChecked || !isESignChecked}
            background={backgroundBtn}
            color={textColorBtn}
            onClick={() => {
              resetTermsDraft();
              navigate(M2M_ROUTES.TRANSFER_A);
            }}
          >
            {t("common.continue")}
          </Button>
        </section>
      </Layout.Footer>
    </AuraLayout>
  );
});

export const LandingFlavorAPage = () => <LandingPage flavor="a" />;
export const LandingFlavorBPage = () => <LandingPage flavor="b" />;
